/**
 * A list's rows laid on their days (docs/design/calendar-and-timeline.md, list standard §13): which
 * days a month shows, which window a route is asked for, on which day each row falls in the
 * workspace's zone, how spans stack in a week, and what the agenda lists. Pure, so each is proved
 * without a browser; `calendar.tsx` draws what this decides.
 *
 * One rule, the list's: the days in view are a filter, never the page. The window asked of a route
 * is the days drawn — a month that shows 27 September to 7 November asks for those 42 days, so the
 * grey days at its edges are true too — and nothing here counts a figure.
 */
import { dateDisplay, wallClock } from "./fields.js";
import { CALENDAR_VIEWS } from "./manifest.js";
const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const pad = (n, w = 2) => String(n).padStart(w, '0');
export function isoDay(year, month, day) {
    const d = new Date(Date.UTC(year, month - 1, day));
    return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}
function utc(day) {
    const m = DAY.exec(day);
    return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}
export function addDays(day, n) {
    const d = utc(day);
    d.setUTCDate(d.getUTCDate() + n);
    return isoDay(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}
/** 0 Sunday … 6 Saturday, as `weekend_days` counts them. */
export function weekdayOf(day) {
    return utc(day).getUTCDay();
}
export function monthOf(day) {
    const m = DAY.exec(day);
    return { year: Number(m[1]), month: Number(m[2]) };
}
export function shiftMonth(month, by) {
    const n = month.year * 12 + (month.month - 1) + by;
    return { year: Math.floor(n / 12), month: (n % 12) + 1 };
}
export function sameMonth(a, b) {
    return a.year === b.year && a.month === b.month;
}
/**
 * The first day of the workspace's week: the first working day after its weekend (Friday and
 * Saturday off → Sunday; Saturday and Sunday off → Monday). No weekend, or every day off: Sunday.
 */
export function weekStartOf(weekend = [5, 6]) {
    const off = new Set((weekend ?? [5, 6]).filter((d) => Number.isInteger(d) && d >= 0 && d <= 6));
    if (off.size === 0 || off.size === 7)
        return 0;
    for (let d = 0; d < 7; d += 1)
        if (!off.has(d) && off.has((d + 6) % 7))
            return d;
    return 0;
}
/** The weekend as the workspace said it, Friday and Saturday until it has. */
export function weekendOf(display = dateDisplay()) {
    return display.weekend ?? [5, 6];
}
/** The 42 days a month view draws: six whole weeks from the week holding the 1st, starting on `start`. */
export function monthGrid(month, start) {
    const first = isoDay(month.year, month.month, 1);
    const lead = (weekdayOf(first) - start + 7) % 7;
    const from = addDays(first, -lead);
    return Array.from({ length: 42 }, (_, i) => addDays(from, i));
}
/** The window a month asks of a route: every day it draws, not only the month's own. */
export function monthWindow(month, start) {
    const days = monthGrid(month, start);
    return { from: days[0], to: days[41] };
}
export function sameWindow(a, b) {
    return Boolean(a && b && a.from === b.from && a.to === b.to);
}
/** Today on the workspace's clock: its zone, else the reader's own. */
export function todayIn(zone = dateDisplay().timeZone, now = new Date()) {
    const w = wallClock(now, zone);
    return isoDay(w.year, w.month, w.day);
}
/** The month a calendar opens on: this one, on the workspace's clock. */
export function openingMonth(zone = dateDisplay().timeZone, now = new Date()) {
    return monthOf(todayIn(zone, now));
}
/**
 * The window a list on a `source.window` route is first asked for — the month it opens on, as the
 * calendar draws it — or null when the route takes none. The host asks this before the list is
 * drawn, so the first answer is already the month in view.
 */
export function openingWindow(manifest, display = dateDisplay(), now = new Date()) {
    if (!manifest.source?.window)
        return null;
    return monthWindow(openingMonth(display.timeZone, now), weekStartOf(display.weekend));
}
/** A read's query with the window added: `from` and `to` beside what the link and the view already ask. */
export function windowQuery(query, window) {
    if (!window)
        return query;
    const pair = `from=${encodeURIComponent(window.from)}&to=${encodeURIComponent(window.to)}`;
    return query ? `${query}&${pair}` : `?${pair}`;
}
/**
 * The day a value falls on: a `YYYY-MM-DD` as it is, a moment on the workspace's clock (a meeting at
 * 23:30 UTC is tomorrow in Riyadh). Null for what is not a day — never a day guessed for it.
 */
export function dayOf(value, zone = dateDisplay().timeZone) {
    if (typeof value !== 'string' || !value)
        return null;
    if (DAY.test(value))
        return value;
    if (!/^\d{4}-\d{2}-\d{2}[T ]\d/.test(value))
        return null;
    const at = new Date(value);
    if (Number.isNaN(at.getTime()))
        return null;
    const w = wallClock(at, zone);
    return isoDay(w.year, w.month, w.day);
}
/** The time of a moment on the 12-hour clock — «9:30 ص», «2:00 PM» — in western digits. Null for a plain day. */
export function timeWords(value, locale, zone = dateDisplay().timeZone) {
    if (typeof value !== 'string' || DAY.test(value) || !/^\d{4}-\d{2}-\d{2}[T ]\d/.test(value))
        return null;
    const at = new Date(value);
    if (Number.isNaN(at.getTime()))
        return null;
    return at.toLocaleTimeString(locale === 'ar' ? 'ar-u-nu-latn' : locale, { hour: 'numeric', minute: '2-digit', hour12: true, ...(zone ? { timeZone: zone } : {}) });
}
/**
 * Every row that falls on a day, in the order a day lists them: by its first day, the longer span
 * first (so a week's bars stack the same way on every day), then by its time. A row with no day is
 * left out — said by the caller, never placed on a day it does not have.
 */
export function placeRows(rows, spec, opts) {
    const zone = opts.zone === undefined ? dateDisplay().timeZone : opts.zone;
    const out = [];
    rows.forEach((row, i) => {
        const start = dayOf(row[spec.date], zone);
        if (!start)
            return;
        const ends = spec.end ? dayOf(row[spec.end], zone) : null;
        const end = ends && ends > start ? ends : start;
        const time = spec.time ? timeWords(row[spec.date], opts.locale, zone) : null;
        out.push({ row, key: opts.keyOf(row, i), start, end, time, at: i });
    });
    const length = (p) => utc(p.end).getTime() - utc(p.start).getTime();
    // Within a day: the whole-day rows first, then by the moment itself (never by its words, which
    // would put «10:00 ص» before «9:00 ص»).
    const moment = (p) => (p.time === null ? -Infinity : new Date(String(p.row[spec.date])).getTime());
    out.sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : length(b) - length(a) || moment(a) - moment(b) || a.at - b.at));
    return out.map(({ at: _at, ...p }) => p);
}
export function inWindow(p, window) {
    return p.start <= window.to && p.end >= window.from;
}
/**
 * One week of a month: each row's piece of it put in the first lane free across all its days, so a
 * span reads as one bar and every day keeps its rows in the same rows. At most `max` lanes are
 * drawn; what falls below is counted per day and opened as that day's agenda.
 */
export function layWeek(days, items, max = 3) {
    const first = days[0];
    const last = days[days.length - 1];
    const lanes = [];
    const segments = [];
    const hidden = days.map(() => 0);
    for (const item of items) {
        if (item.start > last || item.end < first)
            continue;
        const from = item.start < first ? first : item.start;
        const to = item.end > last ? last : item.end;
        const col = days.indexOf(from);
        const end = days.indexOf(to);
        let lane = 0;
        while (lanes[lane]?.slice(col, end + 1).some(Boolean))
            lane += 1;
        lanes[lane] ??= days.map(() => false);
        for (let c = col; c <= end; c += 1)
            lanes[lane][c] = true;
        if (lane >= max) {
            for (let c = col; c <= end; c += 1)
                hidden[c] += 1;
            continue;
        }
        segments.push({ item, col, span: end - col + 1, lane, before: item.start < first, after: item.end > last });
    }
    return { days, segments, hidden, lanes: Math.min(max, lanes.length) };
}
/** The days of the window that have rows, each with them: a row that began earlier is listed on the window's first day. */
export function agendaOf(items, window) {
    const days = new Map();
    for (const item of items) {
        if (!inWindow(item, window))
            continue;
        const day = item.start < window.from ? window.from : item.start;
        days.set(day, [...(days.get(day) ?? []), item]);
    }
    return [...days.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([day, list]) => ({ day, items: list }));
}
/** The rows on one day: those that start, run through or end on it. */
export function rowsOnDay(items, day) {
    return items.filter((p) => p.start <= day && p.end >= day);
}
/** Each day of the window with the occasions on it. */
export function holidaysByDay(holidays, window) {
    const out = new Map();
    for (const h of holidays) {
        if (!DAY.test(h.from) || !DAY.test(h.to) || h.to < window.from || h.from > window.to)
            continue;
        for (let d = h.from < window.from ? window.from : h.from; d <= h.to && d <= window.to; d = addDays(d, 1))
            out.set(d, [...(out.get(d) ?? []), h]);
    }
    return out;
}
/** The years a window touches: the holidays are read once per year in view. */
export function yearsOf(window) {
    const a = monthOf(window.from).year;
    const b = monthOf(window.to).year;
    return a === b ? [a] : [a, b];
}
/** The language a Hijri month is named in: the reader's (HRM-19) — Arabic names on an English page were the fault. */
function hijriLanguage(locale) {
    return /^ar\b/i.test(locale) ? 'ar-SA' : /^en\b/i.test(locale) ? 'en-GB' : locale;
}
function hijriParts(day, reference, locale = 'ar') {
    try {
        const calendar = reference === 'tabular' ? 'islamic-civil' : 'islamic-umalqura';
        const parts = new Intl.DateTimeFormat(`${hijriLanguage(locale)}-u-ca-${calendar}-nu-latn`, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).formatToParts(utc(day));
        const of = (t) => parts.find((p) => p.type === t)?.value ?? '';
        const out = { day: of('day'), month: of('month'), year: of('year') };
        return out.day && out.month && out.year ? out : null;
    }
    catch {
        // An engine without the calendar data answers nothing rather than answering wrongly.
        return null;
    }
}
/** The Hijri day of the month under a Gregorian day — «20». Null where the engine has no Hijri calendar. */
export function hijriDayOf(day, reference = dateDisplay().reference) {
    return hijriParts(day, reference)?.day ?? null;
}
/** The Hijri day in full — «20 ربيع الآخر 1448». */
export function hijriDayWords(day, reference = dateDisplay().reference, locale = 'ar') {
    const p = hijriParts(day, reference, locale);
    return p ? `${p.day} ${p.month} ${p.year}` : null;
}
/** The Hijri months a Gregorian month runs across — «ربيع الآخر – جمادى الأولى 1448». */
export function hijriMonthWords(month, reference = dateDisplay().reference, locale = 'ar') {
    const first = hijriParts(isoDay(month.year, month.month, 1), reference, locale);
    const last = hijriParts(addDays(isoDay(month.year, month.month + 1, 1), -1), reference, locale);
    if (!first || !last)
        return null;
    if (first.month === last.month && first.year === last.year)
        return `${first.month} ${first.year}`;
    return first.year === last.year ? `${first.month} – ${last.month} ${last.year}` : `${first.month} ${first.year} – ${last.month} ${last.year}`;
}
/** The Gregorian month and year, the reader's way, western digits — «أكتوبر 2026». */
export function monthWords(month, locale) {
    return utc(isoDay(month.year, month.month, 1)).toLocaleDateString(locale === 'ar' ? 'ar-u-nu-latn' : locale, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}
/** A day's name — «الأحد», «Sun». */
export function weekdayWords(day, locale, width = 'long') {
    return utc(day).toLocaleDateString(locale === 'ar' ? 'ar-u-nu-latn' : locale, { weekday: width, timeZone: 'UTC' });
}
/** A day in full, for a heading or a reader — «الخميس 22 أكتوبر 2026». */
export function dayWords(day, locale) {
    return utc(day).toLocaleDateString(locale === 'ar' ? 'ar-u-nu-latn' : locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
}
/** The day of the month, western digits. */
export function dayNumber(day) {
    return String(Number(day.slice(8, 10)));
}
/** A span of days as words — «12 – 16 أكتوبر». */
export function spanWords(from, to, locale) {
    const fmt = (d, withMonth) => utc(d).toLocaleDateString(locale === 'ar' ? 'ar-u-nu-latn' : locale, { day: 'numeric', ...(withMonth ? { month: 'long' } : {}), timeZone: 'UTC' });
    if (from === to)
        return fmt(from, true);
    return from.slice(0, 7) === to.slice(0, 7) ? `${fmt(from, false)} – ${fmt(to, true)}` : `${fmt(from, true)} – ${fmt(to, true)}`;
}
/** The views a calendar offers, as this version draws them: the declared ones it knows, the first opening. */
export function calendarViews(spec) {
    const declared = spec?.views?.length ? spec.views : CALENDAR_VIEWS;
    const drawn = declared.filter((v) => CALENDAR_VIEWS.includes(v));
    return drawn.length ? drawn : ['month'];
}
//# sourceMappingURL=calendar-model.js.map